//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025, 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Close } from '@carbon/icons-react';
import LoopLogo from '../../icons/loop_logo.png';
import FocusLock from 'react-focus-lock';
import { useConfig } from '../../Config';

interface AboutCardProps {
  onClose: () => void;
}

const AboutCard: React.FC<AboutCardProps> = ({ onClose }) => {
  const { t } = useTranslation();
  const { services } = useConfig();

  const platform_version = services?.platformVersionNumber?.version;
  const build_number = services?.platformBuildNumber?.version;

  return (
    <div
      className="fixed inset-0 z-[1300] flex cursor-default items-center justify-center bg-black/20"
      id="id-modalOverlay"
      data-testid="modalOverlay"
    >
      <FocusLock>
        <div
          className="mx-4 w-full max-w-md rounded-lg bg-white text-gray-900 shadow-xl"
          id="id-modalContent"
          data-testid="modalContent"
        >
          <div
            className="flex items-center justify-between border-b border-gray-200 px-6 py-4"
            id="id-about-card-header"
            data-testid="about-card-header"
          >
            <h2 className="m-0 text-lg font-semibold text-gray-900">
              {t('about-devops-loop')}
            </h2>
            <button
              onClick={onClose}
              className="cursor-pointer rounded border-none bg-transparent p-1 text-gray-500 transition-colors hover:bg-gray-100"
              id="id-closeButton"
              data-testid="closeButton"
              title={t('close')}
            >
              <Close size={20} />
            </button>
          </div>
          <div className="p-6" id="id-aboutBody" data-testid="aboutBody">
            <div
              className="mb-6 flex justify-center"
              id="id-about-card-logo-container"
              data-testid="about-card-logo-container"
            >
              <img
                src={LoopLogo}
                className="h-auto w-full max-w-[300px] object-contain"
                id="id-LoopLogo"
                data-testid="LoopLogo"
                alt={t('titleTab')}
              />
            </div>
            <div
              className="mb-6 text-center"
              id="id-about-card-version-info"
              data-testid="about-card-version-info"
            >
              <p className="mb-1 text-sm text-gray-600">
                {t('Build')} {platform_version}
              </p>
              <p className="mb-0 text-sm text-gray-600">
                {t('build')} {build_number}
              </p>
            </div>
            <div
              className="flex flex-col gap-2 text-center"
              id="id-about-card-copyright"
              data-testid="about-card-copyright"
            >
              <p className="m-0 text-sm font-medium text-gray-700">
                © {t('loop-all-rights-reserved')}
              </p>
              <p className="m-0 text-xs text-gray-500">{t('Copyright-HCL')}</p>
              <p className="m-0 text-xs text-gray-500">{t('Trademark')}</p>
            </div>
          </div>
          <div
            className="rounded-b-lg border-t border-gray-200 bg-gray-50 px-6 py-4"
            id="id-about-card-footer"
            data-testid="about-card-footer"
          >
            <button
              onClick={onClose}
              className="w-full cursor-pointer rounded border-none bg-[#3C91FF] py-2 text-sm font-medium text-white transition-colors hover:bg-[#2b7de6]"
            >
              {t('Close')}
            </button>
          </div>
        </div>
      </FocusLock>
    </div>
  );
};

export default AboutCard;
